import { DurationEditorConfig } from '../../components/state-editors/duration-editor/duration-editor.component';
import { NamedValueEditorConfig } from '../../components/state-editors/named-value-editor/named-value-editor.component';
import { NumberEditorConfig } from '../../components/state-editors/number-editor/number-editor.component';
import { SliderEditorConfig } from '../../components/state-editors/slider-editor/slider-editor.component';
import { InputHelperArguments } from '../../components/state-editors/state-edition-modal/state-edition-modal.component';
import { TextEditorConfig } from '../../components/state-editors/text-state-editor/text-editor.component';
import { PropertyMetadata } from '../../models/DeviceDetail';
import { Editor } from '../../models/Editor';
import { UiProfileProperty } from '../../models/UiProfile';

import { buildDateEditor } from './builders/buildDateEditor';
import { buildDurationEditor } from './builders/buildDurationEditor';
import { buildNamedValueEditor } from './builders/buildNamedValueEditor';
import { buildNumberEditor } from './builders/buildNumberEditor';
import { buildTextEditor } from './builders/buildTextEditor';

export type DeviceEditablePropertyValue = string | number;

export type SectionItemValue = string | number | null;

export interface InputHelper {
    key: string;
    arguments?: InputHelperArguments;
}

export interface SectionItemEditor {
    value: SectionItemValue;
    type?: string;
    inputHelper?: InputHelper;
    warningMessage?: string;
    config?: TextEditorConfig | NamedValueEditorConfig | NumberEditorConfig | SliderEditorConfig | DurationEditorConfig;
}

type EditorBuilder = (
    uiProperty: UiProfileProperty,
    value: DeviceEditablePropertyValue,
    metadata: PropertyMetadata,
) => SectionItemEditor;

// No UI profile declares a slider: the number builder shows one when the range is small enough.
const buildersByType: Partial<Record<Editor['type'], EditorBuilder>> = {
    text: buildTextEditor,
    namedValue: buildNamedValueEditor,
    number: buildNumberEditor,
    duration: buildDurationEditor,
    date: buildDateEditor,
};

export default function buildEditor(
    uiProperty: UiProfileProperty,
    propertyValue: DeviceEditablePropertyValue,
    metadata?: PropertyMetadata,
): SectionItemEditor | undefined {
    const type = uiProperty.editor?.type;
    const editorBuilder = type ? buildersByType[type] : undefined;
    if (!editorBuilder) {
        return undefined;
    }

    const editor = editorBuilder(uiProperty, propertyValue, metadata ?? {});
    if (uiProperty.editor?.warningMessage) {
        editor.warningMessage = uiProperty.editor.warningMessage;
    }
    return editor;
}
